/**
 * Certifications Page (teaser / "coming soon")
 *
 * Previews an upcoming feature: run a standardized certification suite against a
 * *subject* — an eSIM, a modem, or an eSIM × modem combination — and get a
 * pass/fail certificate plus side-by-side comparisons.
 *
 * This page is intentionally non-functional. No backend, no execution. It exists
 * to set expectations and gauge interest. When the real feature lands, the three
 * subject cards become entry points into the certification builder/runner.
 */

import React from 'react';
import {
  ShieldCheck,
  CreditCard,
  Cpu,
  Layers,
  Signal,
  Gauge,
  Phone,
  MessageSquare,
  Globe,
  Radio,
  Sparkles,
  Lock,
  BadgeCheck,
  ArrowRight,
  type LucideIcon,
} from 'lucide-react';
import { AppShell } from '../components/layout/AppShell';
import { Badge, Button, Card } from '@/components/ui';

/** The three things a user can certify. */
interface CertSubject {
  id: string;
  title: string;
  tagline: string;
  description: string;
  icon: LucideIcon;
  /** Short preview of what the suite measures for this subject. */
  checks: string[];
}

const SUBJECTS: CertSubject[] = [
  {
    id: 'esim',
    title: 'Certify an eSIM',
    tagline: 'Is this profile fit for purpose?',
    description:
      'Validate an eSIM profile end-to-end against the standard suite, then keep a shareable pass/fail certificate of exactly what it delivered on a live network.',
    icon: CreditCard,
    checks: ['Attach & registration', 'Data throughput', 'VoLTE / voice', 'SMS delivery', 'Roaming reach'],
  },
  {
    id: 'modem',
    title: 'Certify a Modem',
    tagline: 'Does the hardware match the datasheet?',
    description:
      'Run the full capability suite against a modem model to certify what it actually delivers in the field — not just what the spec sheet claims.',
    icon: Cpu,
    checks: ['RAT coverage (2G–5G)', 'Data plane (QMI)', 'Band support', 'Voice bearer', 'GNSS / positioning'],
  },
  {
    id: 'combination',
    title: 'Certify a Combination',
    tagline: 'Which pairing performs best?',
    description:
      'Pair any eSIM with any modem (and network and location), benchmark the combinations side by side, and let the scorecard surface the best fit.',
    icon: Layers,
    checks: ['eSIM × modem matrix', 'Side-by-side scorecards', 'Network & location pivot', 'Best-fit recommendation'],
  },
];

/** What a standard certification suite checks — drawn from the live tier-0 catalogue. */
interface SuiteCategory {
  label: string;
  icon: LucideIcon;
}

const SUITE_CATEGORIES: SuiteCategory[] = [
  { label: 'Cellular attach & registration', icon: Signal },
  { label: 'Data performance & throughput', icon: Gauge },
  { label: 'Voice & VoLTE / VoNR', icon: Phone },
  { label: 'SMS delivery', icon: MessageSquare },
  { label: '5G NSA reachability', icon: Radio },
  { label: 'Roaming & network selection', icon: Globe },
];

export const Certifications: React.FC = () => {
  return (
    <AppShell>
      <div className="flex flex-col h-full">
        {/* Header */}
        <div className="p-6 border-b border-border">
          <div className="flex items-start justify-between gap-4 flex-wrap">
            <div className="flex items-center space-x-3">
              <div className="p-2 rounded-lg bg-primary-100 dark:bg-primary-900/30">
                <ShieldCheck className="w-6 h-6 text-primary-600 dark:text-primary-400" />
              </div>
              <div>
                <div className="flex items-center gap-2">
                  <h1 className="text-2xl font-bold text-foreground">Certifications</h1>
                  <Badge variant="secondary" className="gap-1">
                    <Sparkles className="w-3 h-3" />
                    Coming soon
                  </Badge>
                </div>
                <p className="text-sm text-muted-foreground">
                  Standardized test suites you run against an eSIM, a modem, or a combination — then compare.
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6">
          <div className="max-w-5xl mx-auto space-y-8">
            {/* Hero / pitch */}
            <Card className="relative overflow-hidden p-8">
              <div className="absolute -right-10 -top-10 opacity-[0.06] pointer-events-none">
                <ShieldCheck className="w-48 h-48 text-primary-600" />
              </div>
              <div className="relative max-w-2xl space-y-3">
                <h2 className="text-xl font-semibold text-foreground">
                  One standard suite. Repeatable, comparable results.
                </h2>
                <p className="text-sm text-muted-foreground leading-relaxed">
                  Define the certification suite once — attach, data, voice, SMS, 5G, roaming — then point it at a
                  subject and let it run on real hardware over live networks. Every run produces the same scorecard,
                  so you can certify a profile, qualify a modem, or benchmark eSIM × modem pairings against each other
                  with confidence.
                </p>
                <div className="flex flex-wrap items-center gap-3 pt-1">
                  <span className="inline-flex items-center gap-1.5 text-xs text-muted-foreground">
                    <BadgeCheck className="w-4 h-4 text-primary-600 dark:text-primary-400" />
                    Pass / fail certificates
                  </span>
                  <span className="inline-flex items-center gap-1.5 text-xs text-muted-foreground">
                    <Layers className="w-4 h-4 text-primary-600 dark:text-primary-400" />
                    Side-by-side comparison
                  </span>
                  <span className="inline-flex items-center gap-1.5 text-xs text-muted-foreground">
                    <Signal className="w-4 h-4 text-primary-600 dark:text-primary-400" />
                    Real modems, live networks
                  </span>
                </div>
              </div>
            </Card>

            {/* Subject cards */}
            <div>
              <h3 className="text-sm font-semibold uppercase tracking-wide text-muted-foreground mb-3">
                What you'll be able to certify
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                {SUBJECTS.map((subject) => {
                  const Icon = subject.icon;
                  return (
                    <Card key={subject.id} className="flex flex-col p-5 h-full">
                      <div className="flex items-center justify-between mb-3">
                        <div className="p-2 rounded-lg bg-primary-100 dark:bg-primary-900/30">
                          <Icon className="w-5 h-5 text-primary-600 dark:text-primary-400" />
                        </div>
                        <Badge variant="outline" className="text-[10px] uppercase tracking-wide">
                          Soon
                        </Badge>
                      </div>
                      <h4 className="text-base font-semibold text-foreground">{subject.title}</h4>
                      <p className="text-xs font-medium text-primary-600 dark:text-primary-400 mb-2">
                        {subject.tagline}
                      </p>
                      <p className="text-sm text-muted-foreground leading-relaxed flex-1">
                        {subject.description}
                      </p>
                      <ul className="mt-4 space-y-1.5">
                        {subject.checks.map((check) => (
                          <li key={check} className="flex items-center gap-2 text-xs text-muted-foreground">
                            <BadgeCheck className="w-3.5 h-3.5 text-primary-600/70 dark:text-primary-400/70 shrink-0" />
                            {check}
                          </li>
                        ))}
                      </ul>
                      <Button variant="outline" className="mt-5 w-full gap-2" disabled>
                        <Lock className="w-4 h-4" />
                        Coming soon
                      </Button>
                    </Card>
                  );
                })}
              </div>
            </div>

            {/* Suite preview */}
            <Card className="p-6">
              <div className="flex items-center gap-2 mb-1">
                <h3 className="text-base font-semibold text-foreground">Inside the standard suite</h3>
              </div>
              <p className="text-sm text-muted-foreground mb-4">
                Each certification runs a curated set of checks — the same building blocks already powering Quick
                Tests and Flows today.
              </p>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                {SUITE_CATEGORIES.map((cat) => {
                  const Icon = cat.icon;
                  return (
                    <div
                      key={cat.label}
                      className="flex items-center gap-3 rounded-lg border border-border bg-muted/40 px-3 py-2.5"
                    >
                      <Icon className="w-4 h-4 text-primary-600 dark:text-primary-400 shrink-0" />
                      <span className="text-sm text-foreground">{cat.label}</span>
                    </div>
                  );
                })}
              </div>
            </Card>

            {/* Footer note */}
            <div className="flex items-center justify-center gap-2 text-sm text-muted-foreground pb-2">
              <span>We're building this now.</span>
              <span className="inline-flex items-center gap-1 text-primary-600 dark:text-primary-400 font-medium">
                Stay tuned <ArrowRight className="w-4 h-4" />
              </span>
            </div>
          </div>
        </div>
      </div>
    </AppShell>
  );
};
